import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb, Link as LinkIcon, AlertTriangle, UserPlus, X, Clock, Sparkles, ExternalLink, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/Button';
interface Suggestion {
  id: string;
  type: 'related' | 'gap' | 'expert' | 'stale';
  title: string;
  description: string;
  timestamp: string;
  actionLabel?: string;
  dismissed?: boolean;
}
interface ProactiveSuggestionsProps {
  isOpen: boolean;
  onClose: () => void;
}
export function ProactiveSuggestions({
  isOpen,
  onClose
}: ProactiveSuggestionsProps) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([{
    id: '1',
    type: 'related',
    title: 'Related work discovered',
    description: "Sarah's team is also working on DNS optimization. Consider collaborating to avoid duplicate work.",
    timestamp: '2h ago',
    actionLabel: 'View their stream'
  }, {
    id: '2',
    type: 'gap',
    title: 'Knowledge gap detected',
    description: '3 people asked about Redis caching this week but no documented answers exist.',
    timestamp: '5h ago',
    actionLabel: 'Document this'
  }, {
    id: '3',
    type: 'expert',
    title: 'Expertise match',
    description: "Based on your recent work, you could help with the 'API Performance Debug' stream.",
    timestamp: '1d ago',
    actionLabel: 'Offer to help'
  }]);
  const [detailsModal, setDetailsModal] = useState<string | null>(null);
  const activeSuggestions = suggestions.filter(s => !s.dismissed);
  const getIcon = (type: string) => {
    switch (type) {
      case 'related':
        return <LinkIcon className="w-4 h-4 text-primary-400" />;
      case 'gap':
        return <AlertTriangle className="w-4 h-4 text-warning-400" />;
      case 'expert':
        return <UserPlus className="w-4 h-4 text-purple-400" />;
      case 'stale':
        return <Clock className="w-4 h-4 text-text-secondary" />;
      default:
        return <Lightbulb className="w-4 h-4 text-warning-400" />;
    }
  };
  const handleDismiss = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSuggestions(prev => prev.map(s => s.id === id ? {
      ...s,
      dismissed: true
    } : s));
  };
  const handleViewDetails = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDetailsModal(id);
  };
  const handleAction = (suggestion: Suggestion) => {
    // Simulate taking action
    alert(`Action: ${suggestion.actionLabel}\n\nThis would navigate to the relevant stream or open a dialog to help with this suggestion.`);
    handleDismiss(suggestion.id, {} as React.MouseEvent);
  };
  const selectedSuggestion = suggestions.find(s => s.id === detailsModal);
  return <AnimatePresence>
      {isOpen && <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={onClose} />

          {/* Dropdown Panel */}
          <motion.div initial={{
        opacity: 0,
        y: -10,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -10,
        scale: 0.95
      }} className="fixed top-16 right-4 w-96 bg-surface-900 border border-surface-600 rounded-xl shadow-2xl z-50 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-surface-600 bg-surface-900/95 backdrop-blur">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-teal-400" />
                <h3 className="font-semibold text-text-primary">AI Insights</h3>
                <span className="bg-teal-500/20 text-teal-400 text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                  {activeSuggestions.length}
                </span>
              </div>
              <button onClick={onClose} className="text-text-muted hover:text-text-primary transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="max-h-[400px] overflow-y-auto">
              {activeSuggestions.length === 0 ? <div className="p-8 text-center">
                  <CheckCircle2 className="w-12 h-12 text-success-500 mx-auto mb-3" />
                  <p className="text-sm text-text-secondary">All caught up!</p>
                  <p className="text-xs text-text-muted mt-1">
                    No new insights right now
                  </p>
                </div> : activeSuggestions.map(item => <div key={item.id} className="p-4 border-b border-surface-600 hover:bg-surface-800/30 transition-colors relative group">
                    <div className="flex gap-3">
                      <div className="mt-1 w-8 h-8 rounded-lg bg-surface-800 flex items-center justify-center flex-shrink-0">
                        {getIcon(item.type)}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-text-primary mb-1">
                          {item.title}
                        </h4>
                        <p className="text-xs text-text-secondary leading-relaxed mb-3">
                          {item.description}
                        </p>

                        <div className="flex gap-2">
                          <Button size="sm" variant="secondary" className="h-7 text-xs" onClick={e => handleViewDetails(item.id, e)}>
                            <ExternalLink className="w-3 h-3 mr-1" />
                            {item.actionLabel || 'View Details'}
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-text-muted" onClick={e => handleDismiss(item.id, e)}>
                            Dismiss
                          </Button>
                        </div>
                      </div>
                    </div>
                    <span className="absolute top-4 right-4 text-[10px] text-text-muted">
                      {item.timestamp}
                    </span>
                  </div>)}
            </div>

            <div className="p-3 bg-surface-900/50 text-center border-t border-surface-600">
              <button className="text-xs text-text-muted hover:text-text-primary transition-colors" onClick={() => alert('This would show a full insights history page')}>
                View all insights
              </button>
            </div>
          </motion.div>

          {/* Details Modal */}
          <AnimatePresence>
            {detailsModal && selectedSuggestion && <>
                <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={() => setDetailsModal(null)} />
                <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] bg-surface-900 border border-surface-600 rounded-xl shadow-2xl z-50 overflow-hidden">
                  <div className="p-6 border-b border-surface-600">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-surface-800 flex items-center justify-center flex-shrink-0">
                        {getIcon(selectedSuggestion.type)}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-text-primary mb-1">
                          {selectedSuggestion.title}
                        </h3>
                        <p className="text-sm text-text-secondary">
                          {selectedSuggestion.timestamp}
                        </p>
                      </div>
                      <button onClick={() => setDetailsModal(null)} className="text-text-muted hover:text-text-primary">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="p-6">
                    <p className="text-sm text-text-primary leading-relaxed mb-6">
                      {selectedSuggestion.description}
                    </p>

                    {selectedSuggestion.type === 'related' && <div className="bg-surface-800/50 border border-surface-500 rounded-lg p-4 mb-6">
                        <h4 className="text-sm font-medium text-text-primary mb-2">
                          Related Stream
                        </h4>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center text-xs font-bold">
                            SM
                          </div>
                          <div>
                            <div className="text-sm text-text-primary">
                              DNS Performance Optimization
                            </div>
                            <div className="text-xs text-text-secondary">
                              Sarah's team • 60% complete
                            </div>
                          </div>
                        </div>
                      </div>}

                    {selectedSuggestion.type === 'gap' && <div className="bg-warning-500/10 border border-warning-500/20 rounded-lg p-4 mb-6">
                        <h4 className="text-sm font-medium text-warning-400 mb-2">
                          Impact
                        </h4>
                        <ul className="text-sm text-text-primary space-y-1">
                          <li>• 3 people asked this week</li>
                          <li>• Average 2 hours spent finding answers</li>
                          <li>• Could save 6 hours/week if documented</li>
                        </ul>
                      </div>}

                    <div className="flex gap-3">
                      <Button className="flex-1" onClick={() => handleAction(selectedSuggestion)}>
                        {selectedSuggestion.actionLabel || 'Take Action'}
                      </Button>
                      <Button variant="ghost" onClick={() => {
                  handleDismiss(selectedSuggestion.id, {} as React.MouseEvent);
                  setDetailsModal(null);
                }}>
                        Dismiss
                      </Button>
                    </div>
                  </div>
                </motion.div>
              </>}
          </AnimatePresence>
        </>}
    </AnimatePresence>;
}